import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckIcon from '@mui/icons-material/Check';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ewSmallLogo from '../../../assets/images/ew-small-logo.webp';

const getTrialStartDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const plans = [
  {
    id: 'Live-Webinars-USD-Monthly-50',
    name: 'Launch',
    description: 'Best for small live webinars',
    priceToday: '$1',
    priceMonthly: 44,
    icon: RocketLaunchIcon,
    iconBg: 'rgba(34, 197, 94, 0.12)',
    iconColor: '#22c55e',
    features: [
      '50 Live Attendees',
      'Up to 5 webinars',
      'AI Builder (2 funnels)',
      'Live chat & polls',
      'Standard support',
    ],
    popular: false,
  },
  {
    id: 'All-In-One-USD-Monthly-200',
    name: 'Pro',
    description: 'Best for live, simulive & evergreen',
    priceToday: '$1',
    priceMonthly: 116,
    icon: BoltIcon,
    iconBg: 'rgba(78, 97, 246, 0.12)',
    iconColor: '#4E61F6',
    features: [
      '200 Live + 1,000 Auto Attendees',
      'Unlimited webinars',
      'AI Builder (5 funnels)',
      'EasyCRM + Paid Webinars',
      'Priority support + Manager',
    ],
    popular: true,
  },
  {
    id: 'All-In-One-USD-Monthly-500',
    name: 'Growth',
    description: 'Best for high-volume webinars',
    priceToday: '$1',
    priceMonthly: 144,
    icon: BarChartIcon,
    iconBg: 'rgba(139, 92, 246, 0.12)',
    iconColor: '#8B5CF6',
    features: [
      '500 Live + 2,500 Auto Attendees',
      'Unlimited webinars',
      'AI Builder (10 funnels)',
      'EasyCRM + Paid Webinars',
      'Priority support + Manager',
    ],
    popular: false,
  },
] as const;

export default function PricingPage() {
  const navigate = useNavigate();
  const handleSelectPlan = useCallback(
    (planId: string) => {
      navigate(`/try-ai-flow/payment?plan=${planId}`);
    },
    [navigate]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: { xs: 1.5, sm: 2 },
        py: { xs: 2.5, sm: 4 },
        background:
          'linear-gradient(150deg, #DEEEF1 2.21%, #FFF 50.41%)',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Link
          to="/try-ai-flow"
          style={{
            flexShrink: 0,
            marginBottom: 24,
            display: 'block',
            borderRadius: 8,
          }}
        >
          <img
            src={ewSmallLogo}
            alt="EasyWebinar"
            style={{ display: 'block', height: 36 }}
          />
        </Link>

        {/* 7-Day Trial banner */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: { xs: 1, sm: 2.5 },
            py: { xs: .5, sm: 1 },
            borderRadius: 6,
            bgcolor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            mb: 2.5,
            color: '#15803d',
          }}
        >
          <VerifiedUserOutlinedIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
          <Typography variant="body2" sx={{ fontWeight: 600,fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            7-Day Trial &middot; Cancel Anytime
          </Typography>
        </Box>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            textAlign: 'center',
            mb: 1,
            lineHeight: 1.25,
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
          }}
        >
          Start Your 7-Day Trial to Publish
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mb: { xs: 3, sm: 4 },
            maxWidth: 520,
            fontSize: { xs: '0.85rem', sm: '1rem' },
            px: { xs: 1, sm: 0 },
          }}
        >
          Select a plan to publish your webinar and unlock full access to our powerful
          AI-driven platform.
        </Typography>

        {/* Pricing cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: { xs: 2, sm: 3 },
            width: '100%',
            alignItems: 'stretch',
          }}
        >
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <Paper
                key={plan.id}
                elevation={0}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 5,
                  border: plan.popular
                    ? '1px solid #4E61F6'
                    : '1px solid rgba(0,0,0,0.08)',
                  bgcolor: '#fff',
                  boxShadow: plan.popular
                    ? '0 0 0 1.778px #4F39F6, 0 22.227px 44.453px -10.669px #E0E7FF'
                    : '0 4px 20px rgba(0,0,0,0.06)',
                  p: { xs: 2, sm: 3 },
                  flex: 1,
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      px: 2,
                      py: 0.5,
                      borderRadius: 20,
                      bgcolor: '#4E61F6',
                      boxShadow: '0 2px 8px rgba(78, 97, 246, 0.3)',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '0.04em', fontSize: '0.7rem' }}>
                      MOST POPULAR
                    </Typography>
                  </Box>
                )}

                {/* Icon at top-left in colored bg */}
                <Box
                  sx={{
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    borderRadius: 3.5,
                    bgcolor: plan.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 1, sm: 1.5 },
                  }}
                >
                  <Icon sx={{ fontSize: { xs: 20, sm: 24 }, color: plan.iconColor }} />
                </Box>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 0.25,
                    fontSize: { xs: '1.1rem', sm: '1.5rem' },
                  }}
                >
                  {plan.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {plan.description}
                </Typography>

                {/* Pricing box */}
                <Box
                  sx={{
                    borderRadius: '14.225px',
                    border: '0.889px solid #F3F4F6',
                    background: '#F9FAFB',
                    p: { xs: 1.5, sm: 2 },
                    mb: { xs: 1.5, sm: 2.5 },
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.75rem' }, color: 'text.primary' }}>
                    {plan.priceToday}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, fontWeight: 400, color: 'text.secondary', ml: 0.5 }}>
                    today
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75, display: 'block', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Then{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>${plan.priceMonthly}/month</Box>
                    {' '}starting on{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{getTrialStartDate()}</Box>
                  </Typography>
                </Box>

                {/* Features with circular checkmarks */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5 }, flex: 1, mb: { xs: 1.5, sm: 2.5 } }}>
                  {plan.features.map((feature) => (
                    <Box
                      key={feature}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 22, sm: 26 },
                          height: { xs: 22, sm: 26 },
                          borderRadius: '50%',
                          bgcolor: plan.popular ? '#E0E7FF' : '#F3F4F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: { xs: 13, sm: 15 }, color: plan.popular ? '#4E61F6' : '#6A7282' }} />
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleSelectPlan(plan.id)}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />}
                  sx={{
                    mt: 'auto',
                    py: { xs: 0.875, sm: 1 },
                    borderRadius: '14.225px',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.9375rem' },
                    ...(plan.popular
                      ? {
                          bgcolor: '#4E61F6',
                          color: '#fff',
                          border: 'none',
                          boxShadow: '0 2px 8px rgba(78, 97, 246, 0.25)',
                          '&:hover': {
                            bgcolor: '#3D4FD9',
                            border: 'none',
                            boxShadow: '0 4px 14px rgba(78, 97, 246, 0.35)',
                          },
                        }
                      : {
                          bgcolor: '#fff',
                          color: 'text.primary',
                          borderColor: 'rgba(0,0,0,0.15)',
                          '&:hover': {
                            borderColor: 'rgba(0,0,0,0.3)',
                            bgcolor: 'rgba(0,0,0,0.02)',
                          },
                        }),
                  }}
                >
                  Select {plan.name}
                </Button>
              </Paper>
            );
          })}
        </Box>

        {/* Footer */}
        <Typography
          component={Link}
          to="/try-ai-flow"
          variant="body2"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 4,
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': { color: '#4b5563', textDecoration: 'underline' },
          }}
          onClick={()=>{navigate(`${process.env.REACT_APP_EASYWEBINAR_V2_URL}/ai-webinar/webinar-creation`)}}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          Back to Dashboard
        </Typography>
      </Box>
    </Box>
  );
}
